import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, User, Edit2, Trash2, X, Check } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTime } from '../../utils/dateUtils';

const RepairChat = ({ repairId, disabled = false, onCommentAdded }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const { user } = useAuth();
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (repairId) {
      fetchComments();
    }
  }, [repairId]);

  // Removed automatic scroll to bottom when comments update

  // Removed scrollToBottom helper to prevent auto-scrolling

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/repairs/${repairId}/comments`);
      const data = Array.isArray(response.data?.comments)
        ? response.data.comments
        : Array.isArray(response.data)
          ? response.data
          : [];
      setComments(data);
      setError('');
    } catch (err) {
      console.error('Error fetching repair comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim() || !user) {
      return;
    }

    try {
      setSending(true);
      await axios.post(`/api/repairs/${repairId}/comments`, {
        comment_text: newComment.trim(),
        created_by: user.id
      });
      
      setNewComment('');
      await fetchComments(); // Refresh comments to show the new one
      setError('');
      
      // Call callback to notify parent components (like Activity) to refresh
      if (onCommentAdded) {
        onCommentAdded();
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      setError('Failed to add comment');
    } finally {
      setSending(false);
    }
  };

  const handleStartEdit = (comment) => {
    setEditingCommentId(comment.id);
    setEditingText(comment.comment_text);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingText('');
  };

  const handleSaveEdit = async (commentId) => {
    if (!editingText.trim() || !user || !repairId) {
      return;
    }

    try {
      await axios.put(`/api/repairs/${repairId}/comments/${commentId}`, {
        comment_text: editingText.trim(),
        user_id: user.id
      });
      
      setEditingCommentId(null);
      setEditingText('');
      await fetchComments(); // Refresh to show the updated comment
      setError('');
      
      // Call callback if provided
      if (onCommentAdded) {
        onCommentAdded();
      }
    } catch (err) {
      console.error('Error updating comment:', err);
      setError(err.response?.data?.error || 'Failed to update comment');
    }
  };

  const handleDelete = async (commentId) => {
    if (!user || !repairId) {
      return;
    }

    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      setDeletingCommentId(commentId);
      await axios.delete(`/api/repairs/${repairId}/comments/${commentId}`, {
        params: { user_id: user.id }
      });
      
      await fetchComments(); // Refresh to remove the deleted comment
      setError('');
      
      // Call callback if provided
      if (onCommentAdded) {
        onCommentAdded();
      }
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError(err.response?.data?.error || 'Failed to delete comment');
    } finally {
      setDeletingCommentId(null);
    }
  };

  if (loading) {
    return (
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-4">
          <MessageCircle className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">Comments</h3>
        </div>
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Loading comments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg">
      {/* Header */}
      <div className="flex items-center space-x-2 p-4 border-b border-gray-200 bg-gray-50">
        <MessageCircle className="h-5 w-5 text-gray-600" />
        <h3 className="text-lg font-medium text-gray-900">Comments</h3>
        <span className="text-sm text-gray-500">({comments.length})</span>
      </div>

      {/* Messages Area */}
      <div className="h-64 overflow-y-auto p-4 space-y-3 bg-white">
        {error && (
          <div className="text-center py-2">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        
        {comments.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No comments yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Start a conversation by adding the first comment.
            </p>
          </div>
        ) : (
          comments.map((comment) => {
            // Compare as strings to handle type mismatches (PostgreSQL BIGINT returns as string)
            const isOwnComment = user && String(comment.created_by) === String(user.id);
            const isEditing = editingCommentId === comment.id;
            const isDeleting = deletingCommentId === comment.id;
            
            return (
              <div key={comment.id} className="flex space-x-3">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <User className="h-4 w-4 text-blue-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900">
                        {comment.created_by_name || 'Unknown user'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDateTime(comment.created_at)}
                      </span>
                    </div>
                    
                    {/* Edit/Delete buttons - only show for own comments */}
                    {isOwnComment && !isEditing && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleStartEdit(comment)}
                          className="p-1 text-gray-400 hover:text-naviter-blue transition-colors"
                          title="Edit comment"
                          disabled={isDeleting}
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(comment.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete comment"
                          disabled={isDeleting}
                        >
                          {isDeleting ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent"></div>
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Comment text - editable when editing */}
                  {isEditing ? (
                    <div className="mt-1">
                      <textarea
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border-2 border-naviter-blue/30 rounded-md shadow-sm 
                                 focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent 
                                 resize-none text-sm"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            handleCancelEdit();
                          }
                        }}
                      />
                      <div className="mt-2 flex items-center justify-end space-x-2">
                        <button
                          onClick={handleCancelEdit}
                          className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 
                                   rounded-md transition-colors flex items-center space-x-1"
                        >
                          <X className="h-4 w-4" />
                          <span>Cancel</span>
                        </button>
                        <button
                          onClick={() => handleSaveEdit(comment.id)}
                          disabled={!editingText.trim()}
                          className="px-3 py-1.5 text-sm text-white bg-naviter-blue hover:bg-naviter-blue-dark 
                                   rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                                   focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-naviter-blue
                                   flex items-center space-x-1"
                        >
                          <Check className="h-4 w-4" />
                          <span>Save</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-1">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {comment.comment_text}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      {!disabled && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <form onSubmit={handleSubmit} className="flex space-x-2">
            <div className="flex-1">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-none"
                disabled={sending}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
            </div>
            <button
              type="submit"
              disabled={!newComment.trim() || sending}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-naviter-blue hover:bg-naviter-blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-naviter-blue disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </form>
          <p className="mt-1 text-xs text-gray-500">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      )}
    </div>
  );
};

export default RepairChat;
